import React, { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, 
  DollarSign, 
  TrendingUp, 
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Calendar,
  FileText,
  Trash2,
  BarChart3,
  TrendingDown,
  Package
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Payment, Order } from '../../services/dataService';
import { calculatePaymentStatus } from '../../utils/orderUtils';

interface OrderWithPayments extends Order {
  payments: Payment[];
  totalPaid: number;
  remainingAmount: number;
  paymentStatus: 'settled' | 'unsettled' | 'partial';
}

// Stats Card Component - Following Dashboard Design
interface StatsCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ComponentType<any>;
  gradient: string;
  delay: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, subtitle, icon: Icon, gradient, delay }) => (
  <div className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up ${delay} group`}>
    <div className="flex items-center justify-between mb-4">
      <div className={`${gradient} p-2 sm:p-3 rounded-xl group-hover:rotate-12 transition-transform duration-300`}>
        <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
      </div>
      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 group-hover:rotate-12 transition-transform duration-300" />
    </div>
    <div>
      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium mb-1">
        {title}
      </p>
      <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
        {value}
      </p>
      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
        {subtitle}
      </p>
    </div>
  </div>
);

// Summary Card Component - Following Dashboard Design
interface SummaryCardProps {
  status: string;
  count: number;
  icon: React.ComponentType<any>;
  gradient: string;
  delay: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ status, count, icon: Icon, gradient, delay }) => (
  <div className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up ${delay}`}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white capitalize">
        {status} Orders
      </h3>
      <div className={`${gradient} p-2 rounded-xl`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
      </div>
    </div>
    <div className="text-center">
      <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
        {count}
      </p>
      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
        {status === 'settled' ? 'Fully paid' : 
         status === 'partial' ? 'Partially paid' : 'Pending payment'}
      </p>
    </div>
  </div>
);

// Filters Component - Dark Mode Compatible
interface FiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  dateFilter: string;
  setDateFilter: (filter: string) => void;
  methodFilter: string;
  setMethodFilter: (filter: string) => void;
}

const Filters: React.FC<FiltersProps> = ({
  searchTerm,
  setSearchTerm,
  dateFilter,
  setDateFilter,
  methodFilter,
  setMethodFilter
}) => {
  const dateOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' }
  ];

  const methodOptions = [
    { value: 'all', label: 'All Methods' },
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'digital', label: 'Digital Wallet' }
  ];

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-500">
      <div className="flex flex-col md:flex-row gap-3 md:gap-4">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <div className="search-input-icon">
              <Search className="h-4 w-4 md:h-5 md:w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search by customer or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 sm:pl-10 animate-input-focus"
            />
          </div>
        </div>

        {/* Date Filter */}
        <div className="md:w-40 desktop:w-48">
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white"
          >
            {dateOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Method Filter */}
        <div className="md:w-40 desktop:w-48">
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white"
          >
            {methodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Tab Navigation Component - Dark Mode Compatible
interface TabNavigationProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  ordersWithPayments: OrderWithPayments[];
  settledOrders: OrderWithPayments[];
  partialOrders: OrderWithPayments[];
  unsettledOrders: OrderWithPayments[];
}

const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  setActiveTab,
  ordersWithPayments,
  settledOrders,
  partialOrders,
  unsettledOrders
}) => {
  const tabs = [
    {
      id: 'all',
      name: 'All Payments',
      icon: CreditCard,
      gradient: 'bg-gradient-to-r from-blue-400 to-blue-600',
      count: ordersWithPayments.length
    },
    {
      id: 'settled',
      name: 'Settled',
      icon: CheckCircle,
      gradient: 'bg-gradient-to-r from-green-400 to-green-600',
      count: settledOrders.length
    },
    {
      id: 'partial',
      name: 'Partial',
      icon: Clock,
      gradient: 'bg-gradient-to-r from-yellow-400 to-yellow-600',
      count: partialOrders.length
    },
    {
      id: 'unsettled',
      name: 'Unsettled',
      icon: AlertCircle,
      gradient: 'bg-gradient-to-r from-red-400 to-red-600',
      count: unsettledOrders.length
    }
  ];

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-600">
      {/* Mobile: Vertical Stack */}
      <div className="md:hidden space-y-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-medium'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`${activeTab === tab.id ? tab.gradient : 'bg-gray-200 dark:bg-gray-600'} p-2 rounded-lg`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium">{tab.name}</span>
              </div>
              <span className="text-sm font-bold">{tab.count}</span>
            </button>
          );
        })}
      </div>

      {/* Desktop: Horizontal */}
      <div className="hidden md:flex md:space-x-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-medium'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className={`${activeTab === tab.id ? tab.gradient : 'bg-gray-200 dark:bg-gray-600'} p-2 rounded-lg`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium">{tab.name}</span>
              <span className="text-sm font-bold">({tab.count})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const getPaymentMethodIcon = (method: string) => {
  switch (method) {
    case 'cash':
      return <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />;
    case 'card':
      return <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
    case 'digital':
      return <RefreshCw className="w-4 h-4 text-purple-600 dark:text-purple-400" />;
    default:
      return <CreditCard className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
  }
};

// Payment History Component - Dark Mode Compatible
interface PaymentHistoryProps {
  payments: Payment[];
  onDeletePayment: (paymentId: string, amount: number) => void;
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({ payments, onDeletePayment }) => {
  if (payments.length === 0) {
    return (
      <div className="text-center py-3 md:py-4 text-gray-500 dark:text-gray-400">
        <AlertCircle className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2" />
        <p className="text-xs md:text-sm">No payments recorded for this order</p>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
        <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1" />
        Payment History ({payments.length})
      </h4>
      <div className="space-y-2">
        {payments.map((payment) => (
          <div key={payment.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600 rounded-lg p-2 md:p-3 hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors duration-200">
            <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
              {getPaymentMethodIcon(payment.method)}
              <div className="min-w-0 flex-1">
                <span className="font-semibold text-sm md:text-base desktop:text-lg text-gray-900 dark:text-white">
                  ₹{(payment.amount || 0).toFixed(0)}
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">
                  {payment.method} • {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'Unknown date'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
              {payment.notes && (
                <div className="text-xs text-gray-500 dark:text-gray-400 max-w-16 md:max-w-xs truncate hidden md:block">
                  {payment.notes}
                </div>
              )}
              <button
                onClick={() => onDeletePayment(payment.id!, payment.amount || 0)}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 flex-shrink-0"
                title="Delete payment"
              >
                <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Order Card Component - Dark Mode Compatible
interface OrderCardProps {
  order: OrderWithPayments;
}

const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
  const { tenantData } = useAuth();

  const handleDeletePayment = async (paymentId: string, amount: number) => {
    if (window.confirm(`Are you sure you want to delete this payment of ₹${amount.toFixed(2)}?`)) {
      try {
        await dataService.deletePayment(tenantData!.id, paymentId);
        // Data will update automatically via real-time subscription
      } catch (error) {
        console.error('Error deleting payment:', error);
        alert('Failed to delete payment. Please try again.');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'settled': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'partial': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
      case 'unsettled': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  // Safe defaults for potentially undefined values
  const safeTotal = order.total || 0;
  const safeTotalPaid = order.totalPaid || 0;
  const safeRemainingAmount = order.remainingAmount || 0;
  const safeCustomer = order.customer || 'Unknown Customer';
  const safeCreatedAt = order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Unknown date';

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-3 md:p-4 hover:shadow-lg transition-all duration-300 animate-card-hover">
      {/* Order Header */}
      <div className="flex items-start justify-between mb-3 md:mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 md:space-x-3 mb-2">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white truncate">
              {order.id || 'Unknown Order'}
            </h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.paymentStatus)} flex-shrink-0`}>
              {order.paymentStatus}
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-1 md:space-y-0 text-gray-600 dark:text-gray-300 text-sm">
            <div className="flex items-center space-x-1">
              <User className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span className="truncate">{safeCustomer}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span>{safeCreatedAt}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 desktop:gap-4 mb-3 md:mb-4">
        <div className="bg-gray-50 dark:bg-gray-700/50 p-2 md:p-3 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Order Total</p>
          <p className="text-sm md:text-base desktop:text-lg font-semibold text-gray-900 dark:text-white">₹{safeTotal.toFixed(0)}</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-2 md:p-3 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Paid</p>
          <p className="text-sm md:text-base desktop:text-lg font-semibold text-green-600 dark:text-green-400">₹{safeTotalPaid.toFixed(0)}</p>
        </div>
        <div className={`p-2 md:p-3 rounded-lg ${safeRemainingAmount > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Remaining</p>
          <p className={`text-sm md:text-base desktop:text-lg font-semibold ${safeRemainingAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
            ₹{safeRemainingAmount.toFixed(0)}
          </p>
        </div>
      </div>

      {/* Payment History */}
      <PaymentHistory payments={order.payments || []} onDeletePayment={handleDeletePayment} />
    </div>
  );
};

// Orders List Component
interface OrdersListProps {
  orders: OrderWithPayments[];
  activeTab: string;
  searchTerm: string;
  dateFilter: string;
  methodFilter: string;
}

const OrdersList: React.FC<OrdersListProps> = ({ orders, activeTab, searchTerm, dateFilter, methodFilter }) => {
  if (orders.length === 0) {
    return (
      <div className="text-center py-8 md:py-12">
        <div className="text-gray-400 dark:text-gray-500 mb-4">
          <CreditCard className="w-12 h-12 md:w-16 md:h-16 mx-auto" />
        </div>
        <h3 className="text-lg md:text-xl font-medium text-gray-900 dark:text-white mb-2">
          No orders found
        </h3>
        <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">
          {searchTerm || dateFilter !== 'all' || methodFilter !== 'all'
            ? 'Try adjusting your search or filter criteria.'
            : 'Orders will appear here as payments are processed.'
          }
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {orders.map((order, index) => (
        <OrderCard key={order.id} order={order} />
      ))}
    </div>
  );
};

// Main Payments Component
const Payments: React.FC = () => {
  const { tenantData } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [methodFilter, setMethodFilter] = useState('all');
  
  const [payments, setPayments] = useState<Payment[]>([]);
  const [ordersWithPayments, setOrdersWithPayments] = useState<OrderWithPayments[]>([]);

  // Real-time data states
  const [orders, setOrders] = useState<Order[]>([]);

  // Export payments to CSV
  const exportPaymentsToCSV = useCallback(() => {
    const currentFilteredOrders = ordersWithPayments.filter(order => {
      const matchesSearch = (order.customer && order.customer.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (order.id && order.id.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesTab = activeTab === 'all' || order.paymentStatus === activeTab;
      
      // Date filter logic
      let matchesDate = true;
      if (dateFilter !== 'all' && order.createdAt) {
        const orderDate = new Date(order.createdAt);
        const now = new Date();
        
        switch (dateFilter) {
          case 'today':
            matchesDate = orderDate.toDateString() === now.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = orderDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = orderDate >= monthAgo;
            break;
        }
      }
      
      // Method filter logic
      const matchesMethod = methodFilter === 'all' || order.payments.some(payment => payment.method === methodFilter);
      
      return matchesSearch && matchesTab && matchesDate && matchesMethod;
    });

    if (currentFilteredOrders.length === 0) {
      alert('No payment data to export. Please adjust your filters.');
      return;
    }

    // Define CSV headers
    const headers = [
      'Order ID',
      'Customer Name',
      'Customer Phone',
      'Order Date',
      'Order Total',
      'Payment Date',
      'Payment Amount',
      'Payment Method',
      'Payment Status',
      'Remaining Amount'
    ];

    // Convert payments data to CSV rows
    const csvRows: string[][] = [];
    
    currentFilteredOrders.forEach(order => {
      if (order.payments.length === 0) {
        // Include orders with no payments
        const orderDate = order.createdAt ? new Date(order.createdAt) : new Date();
        csvRows.push([
          order.id || 'N/A',
          order.customer || 'N/A',
          order.customerPhone || 'N/A',
          orderDate.toLocaleDateString(),
          (order.total || 0).toFixed(2),
          'N/A',
          '0.00',
          'N/A',
          'Unpaid',
          (order.total || 0).toFixed(2)
        ]);
      } else {
        // Include each payment for orders with payments
        order.payments.forEach(payment => {
          const orderDate = order.createdAt ? new Date(order.createdAt) : new Date();
          const paymentDate = payment.createdAt ? new Date(payment.createdAt) : new Date();
          
          csvRows.push([
            order.id || 'N/A',
            order.customer || 'N/A',
            order.customerPhone || 'N/A',
            orderDate.toLocaleDateString(),
            (order.total || 0).toFixed(2),
            paymentDate.toLocaleDateString(),
            (payment.amount || 0).toFixed(2),
            payment.method || 'N/A',
            order.paymentStatus === 'settled' ? 'Paid' : 
            order.paymentStatus === 'partial' ? 'Partially Paid' : 'Unpaid',
            order.remainingAmount.toFixed(2)
          ]);
        });
      }
    });

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current date and filter info
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const filterInfo = activeTab !== 'all' ? `_${activeTab}` : '';
      const methodInfo = methodFilter !== 'all' ? `_${methodFilter}` : '';
      const dateInfo = dateFilter !== 'all' ? `_${dateFilter}` : '';
      
      link.setAttribute('download', `payments_${dateStr}${filterInfo}${methodInfo}${dateInfo}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [ordersWithPayments, searchTerm, activeTab, dateFilter, methodFilter]);

  // Listen for navbar actions
  useEffect(() => {
    const handleNavbarAction = (event: CustomEvent) => {
      const action = event.detail;
      if (action === 'export-payments') {
        exportPaymentsToCSV();
      }
    };

    window.addEventListener('navbar-action', handleNavbarAction as EventListener);
    
    return () => {
      window.removeEventListener('navbar-action', handleNavbarAction as EventListener);
    };
  }, [exportPaymentsToCSV]);

  // Calculate orders with payments whenever data changes using standardized logic
  const calculateOrdersWithPayments = useCallback(() => {
    const ordersWithPaymentsData: OrderWithPayments[] = orders.map(order => {
      const orderPayments = payments.filter(payment => payment.orderId === order.id);
      const { totalPaid, remainingAmount, paymentStatus } = calculatePaymentStatus(order, orderPayments);

      return {
        ...order,
        payments: orderPayments,
        totalPaid,
        remainingAmount,
        paymentStatus
      };
    });

    setOrdersWithPayments(ordersWithPaymentsData);
  }, [orders, payments]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!tenantData?.id) return;

    console.log('Setting up real-time subscriptions for payments...');
    setLoading(true);

    const unsubscribeOrders = dataService.subscribeToOrders(tenantData.id, (newOrders) => {
      console.log('Orders updated:', newOrders.length);
      setOrders(newOrders || []);
      setLoading(false);
    });

    const unsubscribePayments = dataService.subscribeToPayments(tenantData.id, (newPayments) => {
      console.log('Payments updated:', newPayments.length);
      setPayments(newPayments || []);
    });

    // Cleanup subscriptions on unmount
    return () => {
      console.log('Cleaning up payments subscriptions...');
      unsubscribeOrders();
      unsubscribePayments();
    };
  }, [tenantData?.id]);

  // Recalculate orders with payments whenever data changes
  useEffect(() => {
    calculateOrdersWithPayments();
  }, [calculateOrdersWithPayments]);

  const filterOrdersByDate = (order: OrderWithPayments) => {
    if (dateFilter === 'all') return true;
    
    if (!order.createdAt) return false;
    
    const orderDate = new Date(order.createdAt);
    const now = new Date();
    
    switch (dateFilter) {
      case 'today':
        return orderDate.toDateString() === now.toDateString();
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return orderDate >= weekAgo;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return orderDate >= monthAgo;
      default:
        return true;
    }
  };

  const filterOrdersByMethod = (order: OrderWithPayments) => {
    if (methodFilter === 'all') return true;
    return order.payments.some(payment => payment.method === methodFilter);
  };

  const filteredOrders = ordersWithPayments.filter(order => {
    const matchesSearch = (order.customer && order.customer.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (order.id && order.id.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTab = activeTab === 'all' || order.paymentStatus === activeTab;
    const matchesDate = filterOrdersByDate(order);
    const matchesMethod = filterOrdersByMethod(order);
    
    return matchesSearch && matchesTab && matchesDate && matchesMethod;
  });

  // Calculate statistics with safe defaults
  const totalRevenue = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  const todayPayments = payments.filter(payment => {
    if (!payment.createdAt) return false;
    const paymentDate = new Date(payment.createdAt);
    const today = new Date();
    return paymentDate.toDateString() === today.toDateString();
  });
  const todayRevenue = todayPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  
  const cardPayments = payments.filter(p => p.method === 'card');
  const cashPayments = payments.filter(p => p.method === 'cash');
  
  const cardRevenue = cardPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const cashRevenue = cashPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  const settledOrders = ordersWithPayments.filter(o => o.paymentStatus === 'settled');
  const partialOrders = ordersWithPayments.filter(o => o.paymentStatus === 'partial');
  const unsettledOrders = ordersWithPayments.filter(o => o.paymentStatus === 'unsettled');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-page-enter">
      {/* Header */}
      <div className="text-center sm:text-left">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          Payment Management
        </h2>
        <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg">
          Track and manage all payment transactions and order settlements
        </p>
      </div>

      {/* Stats Grid - Following Dashboard Design */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Revenue"
          value={`₹${totalRevenue.toFixed(0)}`}
          subtitle={`${payments.length} transactions`}
          icon={DollarSign}
          gradient="bg-gradient-to-r from-green-400 to-green-600"
          delay="animation-delay-100"
        />
        <StatsCard
          title="Today's Revenue"
          value={`₹${todayRevenue.toFixed(0)}`}
          subtitle={`${todayPayments.length} today`}
          icon={TrendingUp}
          gradient="bg-gradient-to-r from-blue-400 to-blue-600"
          delay="animation-delay-200"
        />
        <StatsCard
          title="Card Payments"
          value={`₹${cardRevenue.toFixed(0)}`}
          subtitle={`${cardPayments.length} transactions`}
          icon={CreditCard}
          gradient="bg-gradient-to-r from-purple-400 to-purple-600"
          delay="animation-delay-300"
        />
        <StatsCard
          title="Cash Payments"
          value={`₹${cashRevenue.toFixed(0)}`}
          subtitle={`${cashPayments.length} transactions`}
          icon={Package}
          gradient="bg-gradient-to-r from-orange-400 to-orange-600"
          delay="animation-delay-400"
        />
      </div>

      {/* Summary Cards - Following Dashboard Design */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <SummaryCard
          status="settled"
          count={settledOrders.length}
          icon={CheckCircle}
          gradient="bg-gradient-to-r from-green-400 to-green-600"
          delay="animation-delay-500"
        />
        <SummaryCard
          status="partial"
          count={partialOrders.length}
          icon={Clock}
          gradient="bg-gradient-to-r from-yellow-400 to-yellow-600"
          delay="animation-delay-600"
        />
        <SummaryCard
          status="unsettled"
          count={unsettledOrders.length}
          icon={AlertCircle}
          gradient="bg-gradient-to-r from-red-400 to-red-600"
          delay="animation-delay-700"
        />
      </div>

      {/* Filters */}
      <Filters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        methodFilter={methodFilter}
        setMethodFilter={setMethodFilter}
      />

      {/* Tab Navigation */}
      <TabNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        ordersWithPayments={ordersWithPayments}
        settledOrders={settledOrders}
        partialOrders={partialOrders}
        unsettledOrders={unsettledOrders}
      />

      {/* Orders List */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-800">
        <div className="flex items-center justify-between mb-3 md:mb-6">
          <h2 className="text-lg md:text-xl desktop:text-2xl font-bold text-gray-900 dark:text-white">
            {activeTab === 'all' ? 'All Orders' : 
             activeTab === 'settled' ? 'Settled Orders' :
             activeTab === 'partial' ? 'Partially Paid Orders' :
             'Unsettled Orders'}
          </h2>
          <div className="text-xs md:text-sm desktop:text-base text-gray-600 dark:text-gray-300">
            {filteredOrders.length} orders
          </div>
        </div>

        <OrdersList
          orders={filteredOrders}
          activeTab={activeTab}
          searchTerm={searchTerm}
          dateFilter={dateFilter}
          methodFilter={methodFilter}
        />
      </div>
    </div>
  );
};

export default Payments; 